import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getEngagementPlanTasksThunk } from '@redux/tasks/tasksThunks';
import { EngagementPlan, EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import EngagementPlanDefaultImage from 'assets/images/EngagementPlanDefaultImage.jpg';
import EngagementPlanDefaultImage2 from 'assets/images/EngagementPlanDefaultImage2.png';
import { TasksListItem } from 'components/EngagementPlanSelectionDrawer/components/TasksListItem/TasksListItem';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { countEngagementPlanTasks } from 'utils/taskUtils';
import style from './PlanDetailsArea.scss';

type CustomProps = {
  plan?: EngagementPlan | null;
  setSelectedTask: (task: EngagementPlanTask) => void;
};

type Props = ConnectedProps<typeof connectRedux> & CustomProps;

const PlanDetailsArea: React.FC<Props> = ({ plan, getEngagementPlanTasks, setSelectedTask }) => {
  const [planTasks, setPlanTasks] = useState<EngagementPlanTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const imageLink = plan?.organizationIds?.length ? plan.imageSource || EngagementPlanDefaultImage2 : EngagementPlanDefaultImage;

  const tasksSpecification = countEngagementPlanTasks(plan);
  let lengthDescription = `${plan?.durationDays}-day${plan?.durationDays === 1 ? '' : 's'} plan`;

  if (tasksSpecification.total) {
    if (tasksSpecification.recurring) {
      lengthDescription += ` · ${tasksSpecification.recurring} recurring task${tasksSpecification.recurring === 1 ? '' : 's'}`;
    }

    lengthDescription += ` (${tasksSpecification.total} total task${tasksSpecification.total === 1 ? '' : 's'})`;
  }

  useEffect(() => {
    const fetchPlanTasks = async (planId: string) => {
      setIsLoading(true);

      try {
        const tasks = await getEngagementPlanTasks(planId);
        setPlanTasks(tasks);
      } catch (error) {
        console.error('Failed to load engagement plan tasks:', error);
        setPlanTasks([]);
      } finally {
        setIsLoading(false);
      }
    };

    plan?.id && fetchPlanTasks(plan.id);
  }, [plan?.id]);

  return (
    <div className={style.container}>
      {!plan ? (
        <div className={style.emptyState}>
          <Text variant="md" color={Color.GRAY_700}>
            Select an engagement plan to view its tasks
          </Text>
        </div>
      ) : (
        <>
          <div className={style.sectionHeader}>
            <div className={style.planHeaderImage} style={{ backgroundImage: `url(${imageLink})` }} />
            <div className={style.titleWrapper}>
              <Text variant="h5" weight={500}>
                {plan.title}
              </Text>
              {!!plan.description && <Text variant="md">{plan.description}</Text>}
            </div>
            <Text variant="md" color={Color.GRAY}>
              {lengthDescription}
            </Text>
          </div>
          <div className={style.tasksListContainer}>
            <div className={style.tasksList}>
              {isLoading ? (
                <div className={style.loadingContainer}>
                  <CircularProgress />
                </div>
              ) : (
                planTasks.map((task, index) => <TasksListItem key={index} task={task} duration={plan.durationDays} onClick={setSelectedTask} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const connectRedux = connect((state: ReduxState) => ({}), {
  getEngagementPlanTasks: getEngagementPlanTasksThunk
});

export default compose(connectRedux)(PlanDetailsArea);
